import { useMemo, type ReactNode } from "react";
import { useParams, useNavigate, Link } from "react-router";
import { Table, Menu } from "antd";
import type { ColumnsType } from "antd/es/table";
import { LeftOutlined, RightOutlined } from "@ant-design/icons";
import { useQuery } from "@tanstack/react-query";

import ClinicalIcon from "@/assets/icons/clinical.svg?react";
import { useScopeParams } from "@/hooks/useScopeParams";
import { useFeatureFlag, useTrialFeatureFlag } from "@/hooks/useFeatureFlag";
import { FEATURE_FLAGS } from "@/utils/feature-flag-constants";
import { useUser } from "@/contexts/UserContext";
import { toBackendTrialId } from "@/utils/trialIdMap";
import { apiFetchJson } from "@/utils/apiFetch";
import {
  PortfolioCardShell,
  PortfolioMetricTile,
  PortfolioStatusTag,
} from "@/views/Portfolio/shared";
import NotesTab from "@/views/shared/NotesTab";
import TrialDocumentsTab from "./TrialDocumentsTab";
import TrialSummaryView from "./TrialSummaryView";
import type {
  MasteredSite,
  SiteTrialEntry,
} from "@/views/CrossTrialSiteProfile/types";

type PortfolioTrialApiRow = {
  trial_nct_id: string;
  trial_name: string | null;
  brief_title: string | null;
  protocol_number: string | null;
  phase: string | null;
  status: string | null;
  indication: string | null;
  therapeutic_area: string | null;
  drug_name: string | null;
  investigational_product_name: string | null;
  start_date: string | null;
  completion_date: string | null;
  planned_sites: number | null;
  planned_subjects: number | null;
  planned_countries: number | null;
  actual_subjects: number | null;
  site_count: number;
  active_site_count: number;
  country_count: number;
  // metric_timeseries-derived (nullable — render conditionally)
  enrolled: number | null;
  sites_activated: number | null;
  sites_selected: number | null;
  screened: number | null;
  screen_failed: number | null;
  randomized: number | null;
  entered_treatment: number | null;
  ongoing: number | null;
  completed: number | null;
  early_terminated: number | null;
};

type PaginatedTrials = { results: PortfolioTrialApiRow[]; count: number };

// Both endpoints are portfolio-wide catalogs; this screen renders one trial.
// Unfiltered that was 1.78MB / 883 sites on demo to show 10, so narrow server-side.
async function fetchTrials(trialId: string): Promise<PortfolioTrialApiRow[]> {
  const data = await apiFetchJson<PaginatedTrials | PortfolioTrialApiRow[]>(
    `${import.meta.env.VITE_API_URL}/data/portfolio/portfolio-trials/?page_size=200&trial_id=${encodeURIComponent(trialId)}`,
  );
  return Array.isArray(data) ? data : data.results;
}

async function fetchSites(trialId: string): Promise<MasteredSite[]> {
  return apiFetchJson<MasteredSite[]>(
    `${import.meta.env.VITE_API_URL}/data/portfolio/portfolio-sites/?trial_id=${encodeURIComponent(trialId)}`,
  );
}

function DetailCell({
  label,
  value,
}: {
  label: string;
  value: string | null | undefined;
}) {
  if (!value) return null;
  return (
    <div className="flex flex-col gap-0.5 px-[17px] py-3">
      <span className="text-[11px] font-medium uppercase tracking-wide text-[#A5A5A5]">
        {label}
      </span>
      <span className="text-[13px] text-[#1F2933]">{value}</span>
    </div>
  );
}

type FunnelStage = {
  label: string;
  value: number | null | undefined;
  tone?: string;
};

function FunnelStages({ stages }: { stages: FunnelStage[] }) {
  const present = stages.filter((s) => s.value != null);
  if (present.length === 0) return null;
  const max = Math.max(...present.map((s) => s.value as number), 1);
  return (
    <div className="flex flex-wrap items-end gap-x-1 gap-y-3 px-[18px] py-4">
      {present.map((s, i) => (
        <div key={s.label} className="flex items-end gap-1">
          <div className="flex min-w-[88px] flex-col gap-1">
            <span className="text-[11px] font-medium uppercase tracking-wide text-[#A5A5A5]">
              {s.label}
            </span>
            <span className="text-[18px] font-semibold leading-none text-[#1F2933]">
              {Math.round(s.value as number).toLocaleString()}
            </span>
            <div className="h-1 w-full overflow-hidden rounded-full bg-[#EDF1F4]">
              <div
                className="h-full rounded-full"
                style={{
                  width: `${((s.value as number) / max) * 100}%`,
                  background: s.tone ?? "#0C98D0",
                }}
              />
            </div>
          </div>
          {i < present.length - 1 && (
            <RightOutlined className="mb-2 text-[9px] text-[#C7CDD2]" />
          )}
        </div>
      ))}
    </div>
  );
}

function SectionCard({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) {
  return (
    <div className="overflow-hidden rounded-[8px] border border-[#F0F2F4] bg-white shadow-[0_8px_24px_rgba(15,23,42,0.08)]">
      <div className="flex h-[45px] items-center border-b border-[#F0F2F4] px-[18px]">
        <span className="text-[13px] font-semibold text-[#1F2933]">
          {title}
        </span>
      </div>
      {children}
    </div>
  );
}

function PerformanceView({ trial }: { trial: PortfolioTrialApiRow | null }) {
  if (!trial) {
    return (
      <div className="flex items-center justify-center h-48 text-[#8C8C8C] text-[13px]">
        Trial data not found.
      </div>
    );
  }

  const enrolled = trial.enrolled ?? trial.actual_subjects ?? 0;
  const planned = trial.planned_subjects ?? 0;
  const pct = planned > 0 ? Math.round((enrolled / planned) * 100) : null;
  const drug = trial.drug_name ?? trial.investigational_product_name;
  // Prefer the metric_timeseries # Sites Activated figure, else the full
  // loaded roster. active_site_count is deliberately NOT in this chain: it is
  // "sites whose status currently reads active", which understates a lifetime
  // "Activated Sites" figure whenever statuses are partially loaded (Clario:
  // 100 of CYTB's 229 sites carry a status) or the trial has completed
  // (Citeline: completed sites were activated too).
  const activatedSites =
    trial.sites_activated != null
      ? Math.round(trial.sites_activated)
      : trial.site_count;

  const subjectFunnel: FunnelStage[] = [
    { label: "Screened", value: trial.screened, tone: "#7CB3D6" },
    {
      label: "Enrolled",
      value: trial.enrolled ?? trial.actual_subjects,
      tone: "#0C98D0",
    },
    { label: "Randomized", value: trial.randomized, tone: "#0C98D0" },
    { label: "On Treatment", value: trial.entered_treatment, tone: "#1E8C4A" },
    { label: "Ongoing", value: trial.ongoing, tone: "#1E8C4A" },
    { label: "Completed", value: trial.completed, tone: "#48636F" },
  ];
  const sitePipeline: FunnelStage[] = [
    { label: "Selected", value: trial.sites_selected, tone: "#7CB3D6" },
    { label: "Activated", value: trial.sites_activated, tone: "#0C98D0" },
  ];
  const hasFunnel = subjectFunnel.some((s) => s.value != null);
  const hasPipeline = sitePipeline.some((s) => s.value != null);

  const header = (
    <div className="flex h-[45px] items-center justify-between gap-4 border-b border-[#F0F2F4] px-[18px]">
      <div className="flex min-w-0 items-center gap-2.5">
        {drug && (
          <span className="text-[15px] font-semibold text-[#1F2933] truncate">
            {drug}
          </span>
        )}
        {trial.phase && (
          <span className="inline-flex shrink-0 items-center h-5 rounded-[4px] bg-[#E8EEF2] px-1.5 text-[13px] text-[#48636F] whitespace-nowrap">
            Phase {trial.phase}
          </span>
        )}
      </div>
      {trial.status && (
        <PortfolioStatusTag status={trial.status} color="default" />
      )}
    </div>
  );

  const details = (
    <>
      <DetailCell label="Indication" value={trial.indication} />
      <DetailCell label="Therapeutic Area" value={trial.therapeutic_area} />
      <DetailCell label="Start" value={trial.start_date?.slice(0, 7) ?? null} />
      <DetailCell
        label="Completion"
        value={trial.completion_date?.slice(0, 7) ?? null}
      />
    </>
  );

  return (
    <div className="flex flex-col gap-4">
      <PortfolioCardShell
        header={header}
        metricColumns={3}
        additionalColumns={4}
        additionalContent={details}
      >
        <PortfolioMetricTile
          label="Enrolled"
          value={enrolled}
          suffix={planned > 0 ? `/ ${planned}` : ""}
          progress={pct ?? undefined}
          progressTone="blue"
          note={pct != null ? `${pct}% of target` : undefined}
        />
        <PortfolioMetricTile
          label="Activated Sites"
          value={activatedSites}
          note={
            trial.planned_sites != null
              ? `${trial.planned_sites} planned`
              : undefined
          }
        />
        <PortfolioMetricTile
          label="Countries"
          value={trial.country_count}
          note={
            trial.planned_countries != null
              ? `${Math.round(trial.planned_countries)} planned`
              : undefined
          }
        />
      </PortfolioCardShell>

      {hasFunnel && (
        <SectionCard title="Subject Funnel">
          <FunnelStages stages={subjectFunnel} />
          {(trial.screen_failed != null || trial.early_terminated != null) && (
            <div className="flex flex-wrap gap-6 border-t border-[#F0F2F4] px-[18px] py-3">
              {trial.screen_failed != null && (
                <span className="text-[12px] text-[#8C6B00]">
                  Screen failed:{" "}
                  <b>{Math.round(trial.screen_failed).toLocaleString()}</b>
                </span>
              )}
              {trial.early_terminated != null && (
                <span className="text-[12px] text-[#B4443A]">
                  Early terminated:{" "}
                  <b>{Math.round(trial.early_terminated).toLocaleString()}</b>
                </span>
              )}
            </div>
          )}
        </SectionCard>
      )}

      {hasPipeline && (
        <SectionCard title="Site Pipeline">
          <FunnelStages stages={sitePipeline} />
        </SectionCard>
      )}
    </div>
  );
}

type SiteRow = MasteredSite & {
  trialSiteNumber: string | null;
  trialEnrolled: number | null;
  trialTarget: number | null;
  trialTermed: number | null;
  trialStatus: string | null;
  trialStatusColor: SiteTrialEntry["statusColor"] | null;
};

function SitesView({
  sites,
  trialId,
  buildUrl,
  isLoading,
  isError,
  onRetry,
}: {
  sites: MasteredSite[];
  trialId: string;
  buildUrl: (path?: string) => string;
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
}) {
  const siteProfileHidden = useFeatureFlag(
    FEATURE_FLAGS.SITE_PROFILE_LINKS_HIDDEN,
  );
  const investigatorProfileHidden = useFeatureFlag(
    FEATURE_FLAGS.INVESTIGATOR_PROFILE_LINKS_HIDDEN,
  );
  const rows: SiteRow[] = sites.map((site) => {
    const entry = (site.trials ?? []).find((t) => t.trial_id === trialId);
    return {
      ...site,
      trialSiteNumber: entry?.site_number || null,
      trialEnrolled: entry?.enrolled ?? null,
      trialTarget: entry?.target ?? null,
      trialTermed: entry?.early_termination ?? null,
      trialStatus: entry?.status ?? null,
      trialStatusColor: entry?.statusColor ?? null,
    };
  });

  const columns: ColumnsType<SiteRow> = [
    {
      title: "Site ID",
      key: "site_number",
      width: 90,
      render: (_, row) => (
        <span className="text-[13px] text-[#4B5563]">
          {row.trialSiteNumber ?? "—"}
        </span>
      ),
    },
    {
      // The site, never the investigator: the PI has its own column, and
      // labelling sites by their PI is what made this read as a list of people.
      title: "Site Name",
      key: "site_name",
      render: (_, row) => (
        <div className="flex flex-col gap-0.5">
          {siteProfileHidden ? (
            <span className="text-[13px] font-medium text-[#1F2933]">
              {row.site_name}
            </span>
          ) : (
            <Link
              to={buildUrl(`site-profiles/${row.site_key}`)}
              className="text-[13px] font-medium !text-[#1F2933] hover:!text-[#0C98D0]"
            >
              {row.site_name}
            </Link>
          )}
          <span className="text-[11px] text-[#8C8C8C]">
            {[row.city, row.country].filter(Boolean).join(", ")}
          </span>
        </div>
      ),
    },
    {
      title: "PI",
      key: "investigator_name",
      width: 180,
      render: (_, row) =>
        row.investigator_person_id && !investigatorProfileHidden ? (
          <Link
            to={buildUrl(`investigators/${row.investigator_person_id}`)}
            className="text-[13px] font-medium !text-[#4B5563] hover:!text-[#0C98D0]"
          >
            {row.investigator_name ?? "—"}
          </Link>
        ) : (
          <span className="text-[13px] text-[#4B5563]">
            {row.investigator_name ?? "—"}
          </span>
        ),
    },
    {
      title: "Address",
      key: "address",
      width: 220,
      render: (_, row) => {
        const address = [
          row.profile_address,
          row.city,
          row.profile_state,
          row.profile_postal_code,
          row.country,
        ]
          .filter(Boolean)
          .join(", ");
        return (
          <span className="text-[13px] text-[#4B5563]">{address || "—"}</span>
        );
      },
    },
    {
      title: "Status",
      key: "status",
      width: 120,
      render: (_, row) =>
        row.trialStatus ? (
          <PortfolioStatusTag
            status={row.trialStatus}
            color={row.trialStatusColor ?? "default"}
          />
        ) : (
          <span className="text-[12px] text-[#9CA3AF]">—</span>
        ),
    },
    {
      title: "Enrolled",
      key: "enrolled",
      width: 130,
      align: "center",
      render: (_, row) => (
        <span className="text-[13px] font-semibold text-[#1F2933]">
          {row.trialEnrolled ?? "—"}
          {row.trialTarget != null && (
            <span className="text-[12px] font-normal text-[#6B7280]">
              {" "}
              / {row.trialTarget}
            </span>
          )}
        </span>
      ),
    },
    {
      title: "Termed",
      key: "termed",
      width: 100,
      align: "center",
      render: (_, row) => (
        <span className="text-[13px] text-[#1F2933]">
          {row.trialTermed ?? "—"}
        </span>
      ),
    },
  ];

  // The site list is fetched unpaginated for the whole portfolio and filtered
  // client-side, so a slow or truncated response yields zero rows. Reporting
  // that as "no sites" is wrong and indistinguishable from a real empty set --
  // separate the three states.
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-48 text-[#8C8C8C] text-[13px]">
        Loading sites…
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex h-48 flex-col items-center justify-center gap-2 text-[13px] text-[#8C8C8C]">
        <span>Site data could not be loaded.</span>
        <button
          type="button"
          onClick={onRetry}
          className="rounded-sm border border-[#D9D9D9] px-2 py-1 text-[#064C68] hover:border-[#064C68]"
        >
          Retry
        </button>
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div className="flex items-center justify-center h-48 text-[#8C8C8C] text-[13px]">
        No site data available for this trial.
      </div>
    );
  }

  return (
    <div className="rounded-[8px] border border-[#F0F2F4] bg-white shadow-[0_8px_24px_rgba(15,23,42,0.08)] overflow-hidden">
      <Table<SiteRow>
        dataSource={rows}
        columns={columns}
        rowKey="site_key"
        pagination={false}
        size="middle"
      />
    </div>
  );
}

const MENU_CHILDREN = [
  { key: "performance", label: "Overview" },
  { key: "sites", label: "Sites" },
  { key: "notes", label: "Notes" },
];

/**
 * With PORT_TRIAL_SUMMARY the Overview tab renders the Trial Overview briefing
 * instead of the metric tiles and funnels, so those get their own tab rather
 * than being displaced off the screen. Absent the flag, Overview is unchanged
 * and this tab would only duplicate it, so it is not added.
 */
const PERFORMANCE_MENU_CHILD = { key: "metrics", label: "Performance" };

/**
 * The Documents tab reads eTMF tables that only exist for tenants with an
 * eBinders feed, so it is gated on PORT_DOCUMENTS — without the flag the tab is
 * absent and its route falls through to the Overview view.
 */
const DOCUMENTS_MENU_CHILD = { key: "documents", label: "Documents" };

export default function PortfolioTrialDetailView() {
  const { trialId, tab } = useParams<{ trialId: string; tab?: string }>();
  const { scope, id, buildUrl } = useScopeParams();
  const navigate = useNavigate();
  // "Enter trial" (link into the operational trial dashboard) is feature-flagged
  // per trial, not portfolio-wide: a portfolio mixes trials that carry
  // operational data with trials that are portfolio records only (no
  // participants or visits), and the latter must not offer a way in. Off unless
  // this trial is granted PORT_ENTER_TRIAL — e.g. off for every Takeda trial.
  const canEnterTrial = useTrialFeatureFlag(
    trialId,
    FEATURE_FLAGS.PORT_ENTER_TRIAL,
  );
  const canViewDocuments = useFeatureFlag(FEATURE_FLAGS.PORT_DOCUMENTS);
  const notesHidden = useFeatureFlag(FEATURE_FLAGS.NOTES_HIDDEN);
  // Also per trial, and for the same reason: a portfolio-only record has no
  // dataset for the insight agents to brief on, so there is nothing to roll up.
  const showTrialSummary = useTrialFeatureFlag(
    trialId,
    FEATURE_FLAGS.PORT_TRIAL_SUMMARY,
  );
  // Land on the first trial screen this trial's flags actually enable — a
  // hardcoded target 404s on tenants without that flag (e.g. no subject data).
  const { userInfo } = useUser();
  const trialFlags: string[] =
    (userInfo?.available_trials as any[])?.find(
      (t) => t.id === toBackendTrialId(trialId ?? ""),
    )?.feature_flags ?? [];
  const enterTrialPath = (
    [
      // Trial home first: it's the demo landing narrative (Trial News lives
      // there); subjects/sites remain fallbacks for tenants without the flag.
      [FEATURE_FLAGS.TRIAL_HOME_SCREEN, "home"],
      [FEATURE_FLAGS.SUBJECTS_OVERVIEW, "subjects/overview"],
      [FEATURE_FLAGS.SITES_OVERVIEW, "sites/overview"],
    ] as const
  ).find(([flag]) => trialFlags.includes(flag))?.[1];

  const activeTab = tab ?? "performance";

  // trialId is in the keys because it now changes the response.
  const { data: trials = [], isLoading } = useQuery({
    queryKey: ["portfolio-trials", scope, id, trialId],
    queryFn: () => fetchTrials(trialId!),
    enabled: Boolean(scope && id && trialId),
    staleTime: 5 * 60 * 1000,
  });

  const {
    data: allSites = [],
    isPending: sitesLoading,
    isError: sitesError,
    refetch: refetchSites,
  } = useQuery({
    queryKey: ["portfolio-sites", scope, id, trialId],
    queryFn: () => fetchSites(trialId!),
    enabled: Boolean(scope && id && trialId),
    staleTime: 5 * 60 * 1000,
  });

  const trial = useMemo(
    () => trials.find((t) => t.trial_nct_id === trialId) ?? null,
    [trials, trialId],
  );

  const trialSites = useMemo(
    () =>
      allSites.filter((s) =>
        (s.trials ?? []).some((t) => t.trial_id === trialId),
      ),
    [allSites, trialId],
  );

  const handleMenuSelect = (key: string) => {
    navigate(buildUrl(`trials/${trialId}/${key}`));
  };

  const menuItems = useMemo(() => {
    const listed = notesHidden
      ? MENU_CHILDREN.filter((item) => item.key !== "notes")
      : MENU_CHILDREN;
    const base = showTrialSummary
      ? [listed[0], PERFORMANCE_MENU_CHILD, ...listed.slice(1)]
      : listed;
    // Documents sits directly after Sites, wherever Sites ended up.
    const sitesIndex = base.findIndex((item) => item.key === "sites");
    const children = canViewDocuments
      ? [
          ...base.slice(0, sitesIndex + 1),
          DOCUMENTS_MENU_CHILD,
          ...base.slice(sitesIndex + 1),
        ]
      : base;
    return [
      {
        type: "group" as const,
        label: "TRIAL OVERVIEW",
        key: "group-trial-overview",
        children: children.map((item) =>
          item.key === "sites" && trialSites.length > 0
            ? {
                ...item,
                label: (
                  <span className="flex items-center justify-between">
                    Sites
                    <span className="inline-flex items-center justify-center h-[18px] min-w-[18px] rounded-full bg-[#E8EEF2] px-1.5 text-[11px] font-semibold text-[#48636F]">
                      {trialSites.length}
                    </span>
                  </span>
                ),
              }
            : item,
        ),
      },
    ];
  }, [trialSites.length, canViewDocuments, showTrialSummary, notesHidden]);

  const renderContent = () => {
    if (isLoading) {
      return (
        <div className="flex items-center justify-center h-48 text-[#8C8C8C] text-[13px]">
          Loading…
        </div>
      );
    }
    switch (activeTab) {
      case "performance":
        return showTrialSummary ? (
          <TrialSummaryView
            trialId={trialId!}
            fallback={<PerformanceView trial={trial} />}
          />
        ) : (
          <PerformanceView trial={trial} />
        );
      case "metrics":
        return <PerformanceView trial={trial} />;
      case "sites":
        return (
          <SitesView
            sites={trialSites}
            trialId={trialId!}
            buildUrl={buildUrl}
            isLoading={sitesLoading}
            isError={sitesError}
            onRetry={() => void refetchSites()}
          />
        );
      case "documents":
        return canViewDocuments ? (
          <TrialDocumentsTab trialId={trialId!} buildUrl={buildUrl} />
        ) : (
          <PerformanceView trial={trial} />
        );
      case "notes":
        return notesHidden ? null : (
          <NotesTab entityKey={`trial:${trialId!}`} />
        );
      default:
        return <PerformanceView trial={trial} />;
    }
  };

  const displayName =
    trial?.brief_title ??
    trial?.protocol_number ??
    trial?.trial_nct_id ??
    trialId ??
    "";

  return (
    <div className="flex flex-col h-full">
      {/* Back link */}
      <div className="shrink-0 px-3 pt-2">
        <Link
          to={buildUrl("trials")}
          className="flex items-center gap-1.5 text-[12px] text-[#556C75] hover:text-[#2B4752] py-1 w-fit"
        >
          <LeftOutlined className="text-[10px]" />
          All Trials
        </Link>
      </div>

      <div className="flex flex-1 min-h-0">
        {/* Sidebar */}
        <div className="w-[260px] min-w-[260px] sticky top-0 self-start h-screen flex flex-col gap-2 p-3 pb-6 overflow-y-auto">
          {/* Trial info card */}
          <div
            className="px-3 py-3 flex flex-col gap-1"
            style={{
              borderRadius: 8,
              border: "1px solid rgba(0, 0, 0, 0.04)",
              background: "#FFF",
              boxShadow: "0 2px 15px 4px rgba(0, 0, 0, 0.02)",
            }}
          >
            <div className="flex items-center gap-2 mb-1">
              <div className="flex items-center p-[5px] rounded-[4px] bg-[#2B4752] text-white">
                <ClinicalIcon width={20} height={20} />
              </div>
              <span className="text-[10px] font-semibold text-[#556C75] tracking-wide uppercase">
                Trial
              </span>
            </div>
            <span className="text-[14px] font-semibold text-[#1F2933] leading-tight">
              {displayName}
            </span>
            {trial?.trial_nct_id && displayName !== trial.trial_nct_id && (
              <span className="text-[12px] text-[#8C8C8C]">
                {trial.trial_nct_id}
              </span>
            )}
            {trial?.phase && (
              <span className="text-[12px] text-[#8C8C8C]">
                Phase {trial.phase}
              </span>
            )}
            {trial?.status && (
              <div className="mt-0.5">
                <PortfolioStatusTag status={trial.status} color="default" />
              </div>
            )}
            {trial?.therapeutic_area && (
              <div className="flex flex-wrap gap-1 mt-1.5">
                <span className="inline-flex items-center h-5 rounded-[3px] bg-[#EEF4FF] px-1.5 text-[12px] font-medium text-[#3B5FA0] whitespace-nowrap">
                  {trial.therapeutic_area}
                </span>
              </div>
            )}
            {canEnterTrial &&
              trial &&
              trial.site_count > 0 &&
              enterTrialPath && (
                <Link
                  to={`/trial/${trialId}/${enterTrialPath}`}
                  className="mt-2 flex items-center justify-center gap-1.5 w-full h-[30px] rounded-[6px] bg-[#1E8C4A] text-white text-[12px] font-semibold hover:bg-[#166B38] transition-colors no-underline"
                >
                  Enter trial
                  <RightOutlined className="text-[10px]" />
                </Link>
              )}
          </div>

          {/* Navigation menu */}
          <div
            className="overflow-y-auto overflow-clip"
            style={{
              borderRadius: 8,
              border: "1px solid rgba(0, 0, 0, 0.04)",
              background: "#FFF",
              boxShadow: "0 2px 15px 4px rgba(0, 0, 0, 0.02)",
            }}
          >
            <Menu
              mode="inline"
              selectedKeys={[activeTab]}
              items={menuItems}
              onClick={({ key }) => handleMenuSelect(key)}
              style={{ borderRight: "none", background: "transparent" }}
            />
          </div>
        </div>

        {/* Main content */}
        <div className="flex-1 min-w-0 flex flex-col">
          <div className="flex-1 p-4">{renderContent()}</div>
        </div>
      </div>
    </div>
  );
}
